import { Select, Checkbox } from "antd"
import Button from "../../../../atoms/Button/Button"
import Input from "../../../../atoms/Input/Input"
import { Label } from "../../../../atoms/Label/Label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../atoms/Card/Card"
import Badge from "../../../../atoms/Badge/Badge"
import { INDIAN_CUISINES, COMMON_DIETARY_RESTRICTIONS } from "../../../../constants/diet"
import { usePreferencesStep } from "../../hooks/usePreferencesStep"
import "./PreferencesStep.scss"

// Type definitions
interface Constraints {
  dislikes: string[]
  likes: string[]
  avoid_categories: string[]
  notes: string[]
}

interface MedicalInfo {
  concerning_parameters: string[]
  dietary_restrictions: string[]
  medical_considerations: string[]
}

interface Step2Data {
  diet_type: "vegetarian" | "non_vegetarian" | "vegan" | "eggetarian"
  cuisine_pref: string[]
  constraints: Constraints
  medical_info: MedicalInfo
}

interface PreferencesStepProps {
  initialData: Partial<Step2Data>
  onComplete: (data: Step2Data) => void
  onBack: () => void
  apiData?: any // Add API data prop
  onDataUpdate?: (data: Partial<Step2Data>) => void // Add onDataUpdate prop
  selectedDropdownOption?: string
}

export function PreferencesStep({ initialData, onComplete, onBack, apiData, onDataUpdate, selectedDropdownOption }: PreferencesStepProps) {
  const {
    formData,
    newDislike,
    setNewDislike,
    newLike,
    setNewLike,
    newAvoidCategory,
    setNewAvoidCategory,
    newNote,
    setNewNote,
    newConcerningParam,
    setNewConcerningParam,
    newMedicalConsideration,
    setNewMedicalConsideration,
    normalizeDietaryRestrictions,
    addToArray,
    removeFromArray,
    toggleCuisine,
    toggleDietaryRestriction,
    isFormValid,
    handleSubmit,
    updateDietType,
  } = usePreferencesStep({
    initialData,
    apiData,
    selectedDropdownOption,
    onDataUpdate,
  })

  return (
    <div className="preferences-step-container">
      {/* Diet Type */}
      <Card className="diet-type-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {/* <ChefHat className="w-5 h-5" /> */}
            Diet Type
          </CardTitle>
          <CardDescription>Select your primary dietary preference</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="diet-type-grid">
            <Select
              className='ant-select-selection'
              value={formData.diet_type}
              onChange={(value: any) => updateDietType(value)}
              options={[
                { value: "vegetarian", label: "Vegetarian" },
                { value: "non_vegetarian", label: "Non-Vegetarian" },
                { value: "vegan", label: "Vegan" },
                { value: "eggetarian", label: "Eggetarian" },
              ]}
              placeholder="Select diet type"
            />
          </div>
        </CardContent>
      </Card>

      {/* Cuisine Preferences */}
      <Card className="cuisine-preferences-section">
        <CardHeader>
          <CardTitle>Cuisine Preferences</CardTitle>
          <CardDescription>Select your preferred Indian cuisines (choose multiple)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="cuisine-grid">
            {INDIAN_CUISINES.map((cuisine) => (
              <div key={cuisine} className="cuisine-item">
                <Checkbox
                  checked={formData.cuisine_pref?.includes(cuisine) || false}
                  onChange={(e) => toggleCuisine(cuisine)}
                >
                  {cuisine.replace("_", " ")}
                </Checkbox>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Food Preferences */}
      <Card className="food-preferences-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {/* <Heart className="w-5 h-5" /> */}
            Food Preferences
          </CardTitle>
          <CardDescription>Tell us what you like and dislike</CardDescription>
        </CardHeader>
        <CardContent className="preferences-content">
          {/* Likes */}
          <div className="preference-item">
            <Label className="preference-label">Foods You Like</Label>
            <div className="input-group">
              <div className="input-wrapper">
                <Input
                  placeholder="e.g., paneer, dal, rice"
                  value={newLike}
                  onChange={(e: any) => setNewLike(e.target.value)}
                  onKeyDown={(e: any) => e.key === "Enter" && addToArray("likes", newLike, setNewLike)}
                  type="text"
                  name="newLike"
                  id="newLike"
                />
              </div>
              <Button 
                onClick={() => {
                 
                  addToArray("likes", newLike, setNewLike)
                }} 
                variant="outlined"
                className="add-button"
              >
                Add
              </Button>
            </div>
            <div className="badges-container">
              {formData.constraints?.likes?.map((like: string, index: number) => (
                <Badge
                  key={index}
                  variant="secondary"
                  onClick={() => removeFromArray("likes", index)}
                >
                  {like} ×
                </Badge>
              ))}
            </div>
          </div>

          {/* Dislikes */}
          <div className="preference-item">
            <Label className="preference-label">Foods You Dislike</Label>
            <div className="input-group">
              <div className="input-wrapper">
                <Input
                  placeholder="e.g., bitter gourd, okra"
                  value={newDislike}
                  onChange={(e: any) => setNewDislike(e.target.value)}
                  onKeyDown={(e: any) => e.key === "Enter" && addToArray("dislikes", newDislike, setNewDislike)}
                  type="text"
                  name="newDislike"
                  id="newDislike"
                />
              </div>
              <Button 
                onClick={() => addToArray("dislikes", newDislike, setNewDislike)} 
                variant="outlined"
                className="add-button"
              >
                Add
              </Button>
            </div>
            <div className="badges-container">
              {formData.constraints?.dislikes?.map((dislike: string, index: number) => (
                <Badge
                  key={index}
                  variant="destructive"
                  onClick={() => removeFromArray("dislikes", index)}
                >
                  {dislike} ×
                </Badge>
              ))}
            </div>
          </div>

          {/* Avoid Categories */}
          <div className="preference-item">
            <Label className="preference-label">Categories to Avoid</Label>
            <div className="input-group">
              <div className="input-wrapper">
                <Input
                  placeholder="e.g., refined flour, fried snacks, processed foods"
                  value={newAvoidCategory}
                  onChange={(e: any) => setNewAvoidCategory(e.target.value)}
                  onKeyDown={(e: any) =>
                    e.key === "Enter" && addToArray("avoid_categories", newAvoidCategory, setNewAvoidCategory)
                  }
                  type="text"
                  name="newAvoidCategory"
                  id="newAvoidCategory"
                />
              </div>
              <Button
                onClick={() => addToArray("avoid_categories", newAvoidCategory, setNewAvoidCategory)}
                variant="outlined"
                className="add-button"
              >
                Add
              </Button>
            </div>
            <div className="badges-container">
              {formData.constraints?.avoid_categories?.map((category: string, index: number) => (
                <Badge
                  key={index}
                  variant="secondary"
                  onClick={() => removeFromArray("avoid_categories", index)}
                >
                  {category} ×
                </Badge>
              ))}
            </div>
          </div>

          {/* Additional Notes */}
          <div className="preference-item">
            <Label className="preference-label">Additional Notes</Label>
            <div className="input-group">
              <div className="input-wrapper">
                <Input
                  placeholder="e.g., prefer home-cooked meals, avoid spicy food"
                  value={newNote}
                  onChange={(e: any) => setNewNote(e.target.value)}
                  onKeyDown={(e: any) => e.key === "Enter" && addToArray("notes", newNote, setNewNote)}
                  type="text"
                  name="newNote"
                  id="newNote"
                />
              </div>
              <Button 
                onClick={() => addToArray("notes", newNote, setNewNote)} 
                variant="outlined"
                className="add-button"
              >
                Add
              </Button>
            </div>
            <div className="badges-container">
              {formData.constraints?.notes?.map((note: string, index: number) => (
                <Badge
                  key={index}
                  variant="secondary"
                  onClick={() => removeFromArray("notes", index)}
                >
                  {note} ×
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Medical Information */}
      <Card className="medical-information-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {/* <AlertTriangle className="w-5 h-5" /> */}
            Medical Information
          </CardTitle>
          <CardDescription>Health parameters and dietary restrictions</CardDescription>
        </CardHeader>
        <CardContent className="medical-content">
          {/* Concerning Parameters */}
          <div className="medical-item">
            <Label className="medical-label">Concerning Health Parameters</Label>
            <div className="input-group">
              <div className="input-wrapper">
                <Input
                  placeholder="e.g., vitamin B12 deficiency, high cholesterol"
                  value={newConcerningParam}
                  onChange={(e: any) => setNewConcerningParam(e.target.value)}
                  onKeyDown={(e: any) =>
                    e.key === "Enter" && addToArray("concerning_parameters", newConcerningParam, setNewConcerningParam)
                  }
                  type="text"
                  name="newConcerningParam"
                  id="newConcerningParam"
                />
              </div>
              <Button
                onClick={() => addToArray("concerning_parameters", newConcerningParam, setNewConcerningParam)}
                variant="outlined"
                className="add-button"
              >
                Add
              </Button>
            </div>
            <div className="badges-container">
              {formData.medical_info?.concerning_parameters?.map((param: string, index: number) => (
                <Badge
                  key={index}
                  variant="destructive"
                  onClick={() => removeFromArray("concerning_parameters", index)}
                >
                  {param} ×
                </Badge>
              ))}
            </div>
          </div>

          {/* Dietary Restrictions */}
          <div className="medical-item">
            <Label className="medical-label">Dietary Restrictions</Label>
            <div className="dietary-restrictions-grid">
              {COMMON_DIETARY_RESTRICTIONS.map((restriction) => {
                // Case-insensitive comparison with normalized values
                const normalizedRestrictions = normalizeDietaryRestrictions(formData.medical_info?.dietary_restrictions || [])
                const isChecked = normalizedRestrictions.includes(restriction.toLowerCase())
                return (
                  <div key={restriction} className="restriction-item">
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => toggleDietaryRestriction(restriction)}
                    >
                      {restriction}
                    </Checkbox>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Medical Considerations */}
          <div className="medical-item">
            <Label className="medical-label">Medical Considerations</Label>
            <div className="input-group">
              <div className="input-wrapper">
                <Input
                  placeholder="e.g., HbA1c levels, hypertension, PCOS"
                  value={newMedicalConsideration}
                  onChange={(e: any) => setNewMedicalConsideration(e.target.value)}
                  onKeyDown={(e: any) =>
                    e.key === "Enter" &&
                    addToArray("medical_considerations", newMedicalConsideration, setNewMedicalConsideration)
                  }
                  type="text"
                  name="newMedicalConsideration"
                  id="newMedicalConsideration"
                />
              </div>
              <Button
                onClick={() =>
                  addToArray("medical_considerations", newMedicalConsideration, setNewMedicalConsideration)
                }
                variant="outlined"
                className="add-button"
              >
                Add
              </Button>
            </div>
            <div className="badges-container">
              {formData.medical_info?.medical_considerations?.map((consideration: string, index: number) => (
                <Badge
                  key={index}
                  variant="secondary"
                  onClick={() => removeFromArray("medical_considerations", index)}
                >
                  {consideration} ×
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="navigation-buttons">
        <Button onClick={() => handleSubmit(onComplete)} disabled={!isFormValid()} className="continue-button">
          Continue to Generate Plan
        </Button>
      </div>
    </div>
  )
}